import { Check, Input } from "@frui.ts/bootstrap";
import AdvancedSelect from "@edaz/edazkit/controls/AdvancedSelect";
import FormGroup from "controls/formGroup";
import FormLabel from "controls/formLabel";
import { observer } from "mobx-react-lite";
import React from "react";
import { Col, Form } from "react-bootstrap";
import type EnforcementUserCreateViewModel from "viewModels/enforcement/enforcementUsers/enforcementUserCreateViewModel";
import EnforcementUserDetailViewModel from "viewModels/enforcement/enforcementUsers/enforcementUserDetailViewModel";
import FakeInput from "@edaz/edazkit/controls/FakeInput";
import { formatDateTime } from "utils/localizations";

export interface EnforcementUserFormProps {
  vm: EnforcementUserCreateViewModel | EnforcementUserDetailViewModel;
  children?: React.ReactNode;
}

const EnforcementUserForm = observer((props: EnforcementUserFormProps) => {
  const { vm, children } = props;
  const isEdit = vm instanceof EnforcementUserDetailViewModel;
  const disabled = !vm.isEditable;

  return (
    <>
      {isEdit && (
        <>
          <Form.Row>
            <FormGroup as={Col} md="12" target={vm.item} property="id">
              <FormLabel>{vm.translateAttribute("id")}</FormLabel>
              <Input disabled={isEdit || disabled} />
            </FormGroup>
          </Form.Row>
          <Form.Row>
            <FormGroup as={Col} md="6" target={vm.item} property="createdAt">
              <FormLabel>{vm.translateAttribute("createdAt")}</FormLabel>
              <FakeInput
                className="disabled"
                target={vm.item}
                property="createdAt"
                valueFormatter={value => formatDateTime(value)}
              />
            </FormGroup>
            <FormGroup as={Col} md="6" target={vm.item} property="createdBy">
              <FormLabel>{vm.translateAttribute("createdBy")}</FormLabel>
              <Input disabled={isEdit || disabled} />
            </FormGroup>
          </Form.Row>
          <Form.Row>
            <FormGroup as={Col} md="6" target={vm.item} property="updatedAt">
              <FormLabel>{vm.translateAttribute("updatedAt")}</FormLabel>
              <FakeInput
                className="disabled"
                target={vm.item}
                property="updatedAt"
                valueFormatter={value => formatDateTime(value)}
              />
            </FormGroup>
            <FormGroup as={Col} md="6" target={vm.item} property="updatedBy">
              <FormLabel>{vm.translateAttribute("updatedBy")}</FormLabel>
              <Input disabled={isEdit || disabled} />
            </FormGroup>
          </Form.Row>
        </>
      )}
      <Form.Row>
        {isEdit && (
          <>
            <FormGroup as={Col} md="6" target={vm.item} property="userType">
              <FormLabel>{vm.translateAttribute("userType")}</FormLabel>
              <AdvancedSelect
                target={vm.item}
                property="userType"
                items={vm.enforcementUserTypeOptions}
                allowEmpty={true}
                disabled={disabled}
                placeholder={vm.translateGeneral("select")}
              />
            </FormGroup>
            <FormGroup as={Col} md="6" target={vm.item} property="code">
              <FormLabel>{vm.translateAttribute("code")}</FormLabel>
              <Input placeholder={vm.translate("fillCode")} disabled={disabled} />
            </FormGroup>
          </>
        )}
        {!isEdit && (
          <>
            <FormGroup as={Col} md="3" target={vm.item} property="code">
              <FormLabel>{vm.translateAttribute("code")}</FormLabel>
              <Input placeholder={vm.translate("fillCode")} />
            </FormGroup>
            <FormGroup as={Col} md="3" target={vm.item} property="pin">
              <FormLabel>{vm.translateAttribute("pin")}</FormLabel>
              <Input type="password" autoComplete="new-password" disabled={disabled} placeholder={vm.translate("fillPin")} />
            </FormGroup>
            <FormGroup as={Col} md="3" target={vm.item} property="userType">
              <FormLabel>{vm.translateAttribute("userType")}</FormLabel>
              <AdvancedSelect
                target={vm.item}
                property="userType"
                items={vm.enforcementUserTypeOptions}
                allowEmpty={true}
                placeholder={vm.translateGeneral("select")}
              />
            </FormGroup>
          </>
        )}
      </Form.Row>
      <Form.Row>
        <FormGroup className="mb-11" as={Col} md="6" target={vm.item} property="isManager">
          <FormLabel>{vm.translateAttribute("isManager")}</FormLabel>
          <div>
            <Check
              className="d-inline-block mr-4"
              custom
              type="radio"
              target={vm.item}
              property="isManager"
              value={true}
              label={vm.translateGeneral("boolean.true")}
              disabled={disabled}
            />
            <Check
              className="d-inline-block mr-4"
              custom
              type="radio"
              target={vm.item}
              property="isManager"
              value={false}
              label={vm.translateGeneral("boolean.false")}
              disabled={disabled}
            />
          </div>
        </FormGroup>
        {isEdit && !disabled && (
          <>
            <FormGroup className="mb-11" as={Col} md="6" target={vm.item} property="pin">
              <FormLabel>{vm.translateAttribute("pin")}</FormLabel>
              <Input type="password" autoComplete="new-password" disabled={disabled} placeholder={vm.translate("fillPin")} />
            </FormGroup>
          </>
        )}
      </Form.Row>
      {children}
    </>
  );
});

export default EnforcementUserForm;
